'use client';

import { useTranslations } from 'next-intl';
import { Check } from 'lucide-react';
import { AlbumListRow } from './AlbumListRow';
import type { Album } from '../api/album-service';
import type { AlbumJob } from '@/features/design/job-queue';
import { toast } from '@/shared/lib/toast';
import { shareAlbumFile } from '../lib/share-album';
interface AlbumsListProps {
  albums: Array<Album | AlbumJob>;
  highlightedAlbumId: string | null;
  isDeleting: boolean;
  onDelete: (albumId: string) => void;
  onDownload: (albumId?: string) => void;
  conceptTitle?: string | null;
  conceptNumber?: number;
  coverImageUrl?: string | null;
  // Two-up tile grid for the wide two-column album pane: at the content
  // column's width a single column makes each cover gigantic, so lay the cards
  // out two-per-row (three once the column passes ~810px). Breakpoints mirror
  // Apple's adaptive(minimum: 270) — 2 cols ≥540px, 3 cols ≥810px. Each cell
  // stays <800px so AlbumListRow keeps its vertical card. Off (single column)
  // for the narrow single-column settings panel.
  twoUp?: boolean;
}

// Type guard to check if album is from job queue
function isAlbumJob(album: Album | AlbumJob): album is AlbumJob {
  return 'jobType' in album && album.jobType === 'album';
}

// Normalize AlbumJob to Album-like shape for AlbumListRow
function toAlbumProps(album: Album | AlbumJob): Album {
  if (!isAlbumJob(album)) return album;
  return {
    id: album.id,
    status: album.status as Album['status'],
    template: album.albumTemplate ?? 'standard',
    include_blueprints: album.albumIncludeBlueprints ?? false,
    format: album.albumFormat ?? 'A3',
    language: album.albumLanguage ?? 'en',
    whitelabel: false,
    created_at: album.createdAt,
    public_url: album.albumPublicUrl ?? undefined,
    // Timing drives the cover's progress; without it a job row sat at 0%
    // while the album was well under way.
    started_at: album.startedAt,
    duration_predicted: album.durationPredicted,
  };
}

export function AlbumsList({
  albums,
  highlightedAlbumId: _highlightedAlbumId,
  isDeleting: _isDeleting,
  onDelete,
  onDownload,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
  twoUp = false,
}: AlbumsListProps) {
  const t = useTranslations('album');

  const handleCopyPublicLink = async (publicUrl: string | undefined) => {
    if (!publicUrl) {
      toast.error(t('publicUrlNotAvailable'));
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t('publicLinkCopied'), {
        icon: <Check className="h-4 w-4" />,
      });
    } catch (error) {
      toast.error(t('failedToCopyLink'));
      console.error('Copy to clipboard error:', error);
    }
  };

  // Native Web Share — same UX surface as Apple's `square.and.arrow.up`: shares
  // the actual PDF file where supported (mobile/macOS Safari), degrading to a
  // URL share, then clipboard copy on desktop browsers without file share.
  const handleShare = async (
    publicUrl: string | undefined,
    filename: string | undefined
  ) => {
    if (!publicUrl) {
      toast.error(t('publicUrlNotAvailable'));
      return;
    }
    const result = await shareAlbumFile(publicUrl, filename);
    if (result === 'fallback') {
      await handleCopyPublicLink(publicUrl);
    }
  };

  if (albums.length === 0) {
    return null;
  }

  return (
    <div className="pt-2 space-y-2">
      <div
        className={
          twoUp
            ? '@container grid grid-cols-1 gap-4 @[540px]:grid-cols-2 @[810px]:grid-cols-3'
            : 'space-y-4'
        }
      >
        {albums.map((album) => {
          const rowAlbum = toAlbumProps(album);
          // Tap the whole card to open the PDF — completed albums only
          // (in-flight/failed rows have no PDF). Reuses `onDownload`
          // (window.open(public_url)); action icons stopPropagation so they
          // don't double-fire. Apple/Android parity (tap → PDF viewer).
          const isCompleted = rowAlbum.status === 'completed';
          return (
          <AlbumListRow
            key={album.id}
            album={rowAlbum}
            conceptTitle={conceptTitle ?? null}
            conceptNumber={conceptNumber ?? 0}
            coverImageUrl={coverImageUrl ?? null}
            onTap={isCompleted ? () => onDownload(album.id) : undefined}
            onDelete={() => onDelete(album.id)}
            onDownload={() => onDownload(album.id)}
            onCopyLink={
              () => handleCopyPublicLink(
                (isAlbumJob(album) ? album.albumPublicUrl : album.public_url) ?? undefined
              )
            }
            onShare={
              () => handleShare(
                (isAlbumJob(album) ? album.albumPublicUrl : album.public_url) ?? undefined,
                (isAlbumJob(album) ? album.albumFilename : album.filename) ?? undefined
              )
            }
          />
          );
        })}
      </div>
    </div>
  );
}
